import { useEffect, useState } from "react";
import { BadgeCheck, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useMoney } from "@/components/subscriptions/SubscriberCard";

const Tag = ({ real, testId }) => {
  const { t } = useI18n();
  return (
    <span className={`ms-1 inline-flex items-center gap-0.5 rounded-full border px-1.5 text-[0.6rem] font-bold uppercase ${real ? "border-emerald-500/60 text-emerald-300" : "border-amber-400/60 text-amber-200"}`} data-testid={testId}>
      {real ? <BadgeCheck className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{t(real ? "fees.real" : "fees.estimated")}
    </span>
  );
};

const Row = ({ label, children, strong }) => (
  <div className={`flex min-w-0 flex-wrap items-baseline justify-between gap-x-2 text-xs ${strong ? "font-bold text-emerald-200" : "text-neutral-300"}`}>
    <span className="min-w-0 break-words">{label}</span><span className="shrink-0 tabular-nums">{children}</span>
  </div>
);

const MonthCard = ({ m }) => {
  const { t } = useI18n();
  const money = useMoney();
  const f = (v) => money(v, m.currency);
  const id = `fees-month-${m.month}-${m.currency}`;
  return (
    <li className="min-w-0 space-y-1 rounded-xl border border-neutral-700 p-3" data-testid={id}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--gold-bright)]">{m.month} · {m.currency.toUpperCase()} · {m.count}</p>
      <Row label={t("fees.gross")}>{f(m.gross)}</Row>
      <Row label={`− ${t("fees.vat")}`}>{f(m.vat)}</Row>
      <Row label={`− ${t("fees.withheld")}`}>{f(m.fees_withheld)}<Tag real={m.fees_withheld_real} testId={`${id}-withheld-tag`} /></Row>
      <Row label={`− ${t("fees.separate")}`}>{f(m.separate_fees)}<Tag real={m.separate_fees_real} testId={`${id}-separate-tag`} /></Row>
      <Row label={t("fees.netMonth")} strong>{f(m.net)}<Tag real={m.fees_withheld_real && m.separate_fees_real} testId={`${id}-net-tag`} /></Row>
    </li>
  );
};

const PaymentCard = ({ p }) => {
  const { t, fmtDate } = useI18n();
  const money = useMoney();
  const f = (v) => money(v, p.currency);
  return (
    <li className="min-w-0 space-y-1 rounded-xl border border-neutral-800 bg-black/30 p-3" data-testid={`fees-payment-${p.id}`}>
      <p className="break-all text-xs text-neutral-400">{fmtDate(p.at)} · {p.email}</p>
      <Row label={t("fees.paid")}>{f(p.gross)}</Row>
      <Row label={`− ${t("fees.vat")}`}>{f(p.vat)}</Row>
      <Row label={`− ${t("fees.card")}`}>{f(p.fee_withheld)}<Tag real={p.fee_withheld_real} testId={`fees-payment-${p.id}-fee-tag`} /></Row>
      {p.net_received != null && <Row label={t("fees.received")}>{f(p.net_received)}<Tag real /></Row>}
      <Row label={`− ${t("fees.separate")}`}>{f(p.separate_fees_est)}<Tag real={false} /></Row>
      <Row label={t("fees.netAfter")} strong><span data-testid={`fees-payment-${p.id}-net`}>{f(p.net_after_fees)}</span><Tag real={false} /></Row>
      <p className="text-[0.65rem] text-neutral-500">{t(p.bt_status === "available" ? "fees.available" : "fees.pending")}</p>
    </li>
  );
};

export const FeesSection = ({ etag }) => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/fees/payments").then(({ data: d }) => setData(d)).catch(() => {});
  }, [etag]);
  if (!data) return null;
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="fees-section">
      <h2 className="break-words font-display text-base uppercase tracking-wider text-[var(--gold-bright)] md:text-lg">{t("fees.title")}</h2>
      {data.payments.length ? (
        <>
          <ul className="mt-4 grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3">{data.months.map((m) => <MonthCard key={`${m.month}-${m.currency}`} m={m} />)}</ul>
          <ul className="mt-4 grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="fees-payments">{data.payments.map((p) => <PaymentCard key={p.id} p={p} />)}</ul>
        </>
      ) : <p className="mt-3 text-sm text-neutral-400" data-testid="fees-empty">{t("fees.noPayments")}</p>}
      <p className="mt-3 text-xs text-neutral-500">{t("fees.realNote")}</p>
    </section>
  );
};
